import { createElement, useRef, Fragment, ReactElement, useState, useMemo } from 'react';

import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../theme';
import { RangeSliderProps, SliderProps } from './types';
import { BaseSliderProps } from './__internal__/types';
import { setAlpha } from '../../design-tokens';
import { SliderTickLabels, SliderTrackTicks } from './Ticks';
import { SliderScale, LinearSliderScale } from './SliderScale';
import { SliderDefaultFormat, calculatePreciseValue } from './SliderFormat';
import {
  SliderValue,
  RangeSliderValue,
  SliderType,
  RangeSliderType,
  GripType,
} from './SliderValues';
import { SliderValueGrip, SliderValueGripRef } from './ValueGrip';
import { SliderTrackClientBounds } from './useSliderDrag';
import { SliderRange, SliderRangeGrip } from './RangeGrip';
import { SliderStep } from './SliderStep';
import { getSliderTicks, calculateSliderStep } from './__internal__/utils';
import { ReactMouseEvent } from '../../react-synthetic-events';
import { useIsRTL } from '../../locale';
import { postfixTestId } from '../../util/testId';
import { setRef } from '../../util/ref-util';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-Slider-gui-icu.properties';

import { useNCTextLocalizer } from '../../locale/useNCTextLocalizer';
import { useFormatLocale } from '../../locale/formatting';
import cx from 'classnames';
import cn from './Slider.css.json';
import './Slider.css';

createUseTextLocalizer(bundle);

const defaultScale = LinearSliderScale.createFromMinMax(0, 100);

export const Slider = (props: SliderProps) => (
  <BaseSlider
    {...props}
    values={[
      {
        type: 'value',
        valueType: props.valueType,
        value: props.value,
        exclusive: props.exclusive,
        onChange: props.onChange,
        onCommit: props.onCommit,
      },
    ]}
    gripAriaText={props.gripAriaText}
  />
);

export const RangeSlider = (props: RangeSliderProps) => {
  const [sliderGripControlIsVisible, setSliderGripControlIsVisible] = useState(false);
  return (
    <BaseSlider
      {...props}
      sliderGripControlIsVisible={sliderGripControlIsVisible}
      setSliderGripControlIsVisible={setSliderGripControlIsVisible}
      values={[
        {
          type: 'range',
          valueType: props.valueType,
          minValue: props.minValue,
          maxValue: props.maxValue,
          minExclusive: props.minExclusive,
          maxExclusive: props.maxExclusive,
          onChange: props.onChange,
          onCommit: props.onCommit,
        },
      ]}
      minGripAriaText={props.minGripAriaText}
      maxGripAriaText={props.maxGripAriaText}
      rangeAriaText={props.rangeAriaText}
    />
  );
};

const useHtmlId = createUseHtmlId('sliderTickLabels');

class SnapPrecisionScale implements SliderScale {
  private readonly _scale: SliderScale;
  private readonly _step: SliderStep;
  private readonly _numberFormat: Intl.NumberFormat;
  private readonly _disableRoundToStep: boolean | undefined;

  constructor(
    scale: SliderScale,
    step?: SliderStep,
    numberFormat?: Intl.NumberFormat,
    disableRoundToStep?: boolean
  ) {
    this._scale = scale;
    this._step = step ?? 1;
    this._numberFormat = numberFormat ?? new Intl.NumberFormat();
    this._disableRoundToStep = disableRoundToStep;
  }

  value(position: number, didDrag?: boolean) {
    const value = this._scale.value(position);

    // We should only disable step rounding if the following conditions are met:
    // 1. disableRoundToStep is set to true
    // 2. we are not dragging OR
    // 3. we are dragging but we're at the min or max boundary
    //
    // note that we do not disable step rounding if the user is dragging,
    // even if props.disableRoundToStep is set to true, because
    // we have to have some limit on the precision outside of manual entry
    const shouldDisableRoundToStep =
      this._disableRoundToStep &&
      (!didDrag || value === this._scale.minValue || value === this._scale.maxValue);

    // get the precise value based on step
    return calculatePreciseValue(value, this._step, this._numberFormat, shouldDisableRoundToStep);
  }

  get minValue() {
    return this._scale.minValue;
  }

  get maxValue() {
    return this._scale.maxValue;
  }

  position(value: number) {
    return this._scale.position(value);
  }
}

const getCurrentCustomTickLabel = (currValue: number, ticks: number[], tickIds: string[]) => {
  const indexOfTick = ticks.indexOf(currValue);
  return tickIds[indexOfTick];
};

/**
 * This component has limited support for multiple ranges and values. Those cases aren't
 * well tested and probably have some issues.
 */
export const BaseSlider = (props: BaseSliderProps) => {
  const localize = useNCTextLocalizer('Slider');
  const orientation = props.orientation || 'horizontal';
  const arrowGrips = props.arrowGrips ?? false;
  const formatLocale = useFormatLocale();
  const defaultFormatter = new SliderDefaultFormat(
    formatLocale,
    undefined,
    props.disableRoundToStep
  );
  const formatter = props.format || defaultFormatter;
  const scale = useMemo(() => {
    const numberFormat = new Intl.NumberFormat(formatLocale);
    const sliderStep = props.step ?? 1;
    let sliderScale: SliderScale;
    if (props.scale === undefined) {
      sliderScale = defaultScale;
    } else if (Array.isArray(props.scale)) {
      sliderScale = LinearSliderScale.createFromMinMax(
        // calculate precise values for scale min and max based on step
        calculatePreciseValue(props.scale[0], sliderStep, numberFormat, props.disableRoundToStep),
        calculatePreciseValue(props.scale[1], sliderStep, numberFormat, props.disableRoundToStep)
      );
    } else {
      sliderScale = props.scale;
    }
    return new SnapPrecisionScale(
      sliderScale,
      props.step ?? 1,
      numberFormat,
      props.disableRoundToStep
    );
  }, [props.scale, props.step, formatLocale, props.disableRoundToStep]);
  const step = props.step ?? 1;
  const stepMedium = props.stepMedium ?? calculateSliderStep('medium', step, scale);
  const stepLarge = props.stepLarge ?? calculateSliderStep('large', step, scale);

  const ticks = getSliderTicks(scale, props.ticks, props.tickLabels?.length, step);
  const tickIds = useHtmlId(ticks.length);

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const sliderTickLabelsRef = useRef<HTMLDivElement | null>(null);
  const sliderValueGripRef = useRef<SliderValueGripRef | null>(null);

  const [sliderResizeRef] = useResizeObserverRef(() => {
    if (props.orientation === 'vertical' && sliderTickLabelsRef.current) {
      const sliderWidth = sliderRef.current?.getBoundingClientRect().width;
      const sliderGripWidth = sliderValueGripRef.current?.getGripContainerWidth() ?? 0;
      const sliderGripLabelWidth = sliderValueGripRef.current?.getGripLabelElementWidth() ?? 0;
      if (sliderWidth) {
        const maxLabelsWidth = sliderWidth - sliderGripWidth - sliderGripLabelWidth;
        sliderTickLabelsRef.current.style.maxWidth = `${maxLabelsWidth}px`;
      }
    }
  });

  setRef(sliderResizeRef, sliderRef.current);
  const getSliderClientRect = () => {
    return sliderRef.current?.getBoundingClientRect();
  };

  /**
   * - `end` => the end grip in a range
   * - `start` => the start grip in a range
   * - `single` => the grip for a single value (not a range)
   */
  type LastDraggedHandleId = 'end' | 'start' | 'single';
  const [lastDraggedHandleIndex, setLastDraggedHandleIndex] = useState<
    [number, LastDraggedHandleId]
  >([-1, 'single']);

  const refTrack = useRef<HTMLDivElement | null>(null);
  const clientBounds: SliderTrackClientBounds = {
    getSize() {
      if (!refTrack.current) {
        return undefined;
      }
      const bounds = refTrack.current.getBoundingClientRect();
      return { width: bounds.width, height: bounds.height };
    },
  };

  const isRTL = useIsRTL();

  const onTrackClick =
    props.disabled || props.readOnly
      ? undefined
      : (e: ReactMouseEvent) => {
          if (!refTrack.current) {
            return undefined;
          }

          const trackBounds = refTrack.current.getBoundingClientRect();

          const clickPos =
            orientation === 'vertical'
              ? (trackBounds.bottom - e.clientY) / trackBounds.height
              : (isRTL ? trackBounds.right - e.clientX : e.clientX - trackBounds.left) /
                trackBounds.width;
          const clickValue = scale.value(clickPos);

          // find the closest grip
          let closestValueDelta = Infinity,
            closestValue: SliderValue | RangeSliderValue | undefined = undefined,
            closestValueKey: 'minValue' | 'maxValue' | 'value' = 'value';
          for (const sliderValue of props.values) {
            if (sliderValue.type !== 'range') {
              const delta = Math.abs(sliderValue.value - clickValue);
              // S1548296: handle grips that have the same value by:
              //   favoring later grips with the same delta distance if the value is larger.
              if (
                delta < closestValueDelta ||
                (delta === closestValueDelta && clickValue > sliderValue.value)
              ) {
                closestValueDelta = delta;
                closestValue = sliderValue;
                closestValueKey = 'value';
              }
            } else {
              const minDelta = Math.abs(sliderValue.minValue - clickValue),
                maxDelta = Math.abs(sliderValue.maxValue - clickValue);
              // S1548296: handle grips that have the same value by:
              //   favoring later grips with the same delta distance if the value is larger.
              if (
                minDelta < closestValueDelta ||
                (minDelta === closestValueDelta && clickValue > sliderValue.minValue)
              ) {
                closestValueDelta = minDelta;
                closestValue = sliderValue;
                closestValueKey = 'minValue';
              }
              if (
                maxDelta < closestValueDelta ||
                (maxDelta === closestValueDelta && clickValue > sliderValue.maxValue)
              ) {
                closestValueDelta = maxDelta;
                closestValue = sliderValue;
                closestValueKey = 'maxValue';
              }
            }
          }

          if (closestValue !== undefined) {
            if (closestValue.type !== 'range') {
              closestValue.onChange({ value: clickValue });
              if (closestValue.onCommit) {
                closestValue.onCommit({ value: clickValue });
              }
            } else {
              closestValue.onChange({
                minValue: closestValueKey === 'minValue' ? clickValue : closestValue.minValue,
                maxValue: closestValueKey === 'maxValue' ? clickValue : closestValue.maxValue,
              });
              if (closestValue.onCommit) {
                closestValue.onCommit({
                  minValue: closestValueKey === 'minValue' ? clickValue : closestValue.minValue,
                  maxValue: closestValueKey === 'maxValue' ? clickValue : closestValue.maxValue,
                });
              }
            }
          }
        };

  const valueElements: ReactElement[] = props.values.map((value, index) => {
    const [lowerBoundValue, upperBoundValue] = resolveGripBoundary(
      props.values[index - 1] as typeof value | undefined,
      props.values[index + 1] as typeof value | undefined,
      scale
    );

    const shouldHideInput =
      !!props.hideValueLabel || !!props.hideInput || !!props.disabled || !!props.readOnly;

    if (value.type !== 'range') {
      let zIndex = 1;
      if (lastDraggedHandleIndex[0] === index && lastDraggedHandleIndex[1] === 'single') {
        zIndex = 2;
      }

      const numberFormat = new Intl.NumberFormat(formatLocale);
      // calculate precise value for based on step
      const newValue = calculatePreciseValue(
        value.value,
        props.step ?? 1,
        numberFormat,
        props.disableRoundToStep
      );
      // use the calculated value to adjust according to the min and max allowed by the scale
      const adjustedValue = adjustValueForScale(newValue, lowerBoundValue, upperBoundValue);
      const defaultThumbAriaLabel =
        shouldHideInput && props.ariaLabel
          ? props.ariaLabel
          : localize('sliderGrip.sliderThumb.aria');
      return (
        <Fragment key={index}>
          {value.valueType !== SliderType.LessThan ? null : (
            <SliderRange
              testId={postfixTestId(props.testId, 'range')}
              disabled={props.disabled}
              xStyle={{
                orientation,
                minPos: scale.position(lowerBoundValue),
                maxPos: scale.position(adjustedValue),
              }}
              onClick={onTrackClick}
            />
          )}
          <SliderValueGrip
            setSliderGripControlIsVisible={props.setSliderGripControlIsVisible}
            testId={postfixTestId(props.testId, 'valueGrip')}
            disabled={props.disabled}
            readOnly={props.readOnly}
            value={adjustedValue}
            type={GripType.Single}
            minValue={lowerBoundValue}
            maxValue={upperBoundValue}
            inclusive={!value.exclusive}
            onChange={value.onChange}
            onCommit={value.onCommit}
            zIndex={zIndex}
            format={formatter}
            defaultFormat={defaultFormatter}
            disableRoundToStep={props.disableRoundToStep}
            clientBounds={clientBounds}
            scale={scale}
            orientation={orientation}
            hideLabel={!!props.hideValueLabel}
            hideInput={shouldHideInput}
            stepValue={step}
            stepMediumValue={stepMedium}
            stepLargeValue={stepLarge}
            gripLabelClassName={props.gripLabelClassName}
            renderGripInput={props.renderGripInput}
            handleLabelTokens={props.handleLabelTokens}
            onDragStart={
              props.disabled || props.readOnly
                ? undefined
                : () => setLastDraggedHandleIndex([index, 'single'])
            }
            inputAriaLabel={localize('slider.numericValueInput.aria')}
            ariaLabelledBy={shouldHideInput ? props.ariaLabelledBy : undefined}
            ariaLabel={props.gripAriaText ?? defaultThumbAriaLabel}
            ariaDescribedBy={getCurrentCustomTickLabel(value.value, ticks, tickIds)}
            arrowGrips={arrowGrips}
            getSliderClientRect={getSliderClientRect}
            ref={sliderValueGripRef}
            sliderHasTicks={!!props.ticks}
          />
          {value.valueType !== SliderType.GreaterThan ? null : (
            <SliderRange
              testId={postfixTestId(props.testId, 'range')}
              disabled={props.disabled}
              xStyle={{
                orientation,
                minPos: scale.position(adjustedValue),
                maxPos: scale.position(upperBoundValue),
              }}
              onClick={onTrackClick}
            />
          )}
        </Fragment>
      );
    } else {
      if (value.valueType === RangeSliderType.Exclusive) {
        throw new Error('SliderRangeType.Exclusive is not implemented.');
      }

      const numberFormat = new Intl.NumberFormat(formatLocale);
      // calculate precise values based on step
      const newMinValue = calculatePreciseValue(
        value.minValue,
        props.step ?? 1,
        numberFormat,
        props.disableRoundToStep
      );
      const newMaxValue = calculatePreciseValue(
        value.maxValue,
        props.step ?? 1,
        numberFormat,
        props.disableRoundToStep
      );

      // use the calculated values to adjust according to the min and max allowed by the scale
      const adjustedMinValue = adjustValueForScale(newMinValue, lowerBoundValue, upperBoundValue);
      const adjustedMaxValue = adjustValueForScale(newMaxValue, lowerBoundValue, upperBoundValue);

      let startZ = 1,
        endZ = 1;
      if (lastDraggedHandleIndex[1] === 'end') {
        endZ = 2;
      } else if (lastDraggedHandleIndex[1] === 'start') {
        startZ = 2;
      }

      return (
        <Fragment key={index}>
          <SliderValueGrip
            setSliderGripControlIsVisible={props.setSliderGripControlIsVisible}
            testId={postfixTestId(props.testId, 'minValueGrip')}
            disabled={props.disabled}
            readOnly={props.readOnly}
            value={adjustedMinValue}
            minValue={lowerBoundValue}
            maxValue={adjustedMaxValue}
            type={GripType.Minimum}
            inclusive={!value.minExclusive}
            zIndex={startZ}
            format={formatter}
            defaultFormat={defaultFormatter}
            disableRoundToStep={props.disableRoundToStep}
            scale={scale}
            clientBounds={clientBounds}
            orientation={orientation}
            isRange
            hideLabel={!!props.hideValueLabel}
            hideInput={shouldHideInput}
            stepValue={step}
            stepMediumValue={stepMedium}
            stepLargeValue={stepLarge}
            gripLabelClassName={props.gripLabelClassName}
            renderGripInput={props.renderGripInput}
            handleLabelTokens={props.handleLabelTokens}
            inputAriaLabel={localize('slider.numericValueInput.aria')}
            ariaLabel={props.minGripAriaText ?? localize('sliderGrip.sliderMinThumb.aria')}
            arrowGrips={arrowGrips}
            onDragStart={
              props.disabled || props.readOnly
                ? undefined
                : () => setLastDraggedHandleIndex([index, 'start'])
            }
            onChange={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    value.onChange({ minValue: e.value, maxValue: adjustedMaxValue });
                  }
            }
            onCommit={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    if (value.onCommit) {
                      value.onCommit({ minValue: e.value, maxValue: adjustedMaxValue });
                    }
                  }
            }
            getSliderClientRect={getSliderClientRect}
          />
          <SliderRangeGrip
            sliderGripControlIsVisible={props.sliderGripControlIsVisible}
            testId={postfixTestId(props.testId, 'rangeGrip')}
            disabled={props.disabled}
            readOnly={props.readOnly}
            startValue={adjustedMinValue}
            endValue={adjustedMaxValue}
            minValue={lowerBoundValue}
            maxValue={upperBoundValue}
            format={formatter}
            disableRoundToStep={props.disableRoundToStep}
            hideLabel={!!props.hideValueLabel}
            scale={scale}
            clientBounds={clientBounds}
            orientation={orientation}
            stepValue={step}
            stepMediumValue={stepMedium}
            stepLargeValue={stepLarge}
            gripLabelClassName={props.gripLabelClassName}
            handleLabelTokens={props.handleLabelTokens}
            ariaLabel={props.rangeAriaText}
            onPress={onTrackClick}
            onChange={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    value.onChange({ minValue: e.startValue, maxValue: e.endValue });
                  }
            }
            onCommit={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    if (value.onCommit)
                      value.onCommit({ minValue: e.startValue, maxValue: e.endValue });
                  }
            }
          />
          <SliderValueGrip
            setSliderGripControlIsVisible={props.setSliderGripControlIsVisible}
            testId={postfixTestId(props.testId, 'maxValueGrip')}
            disabled={props.disabled}
            readOnly={props.readOnly}
            value={adjustedMaxValue}
            minValue={adjustedMinValue}
            maxValue={upperBoundValue}
            type={GripType.Maximum}
            inclusive={!value.maxExclusive}
            zIndex={endZ}
            format={formatter}
            defaultFormat={defaultFormatter}
            disableRoundToStep={props.disableRoundToStep}
            scale={scale}
            clientBounds={clientBounds}
            orientation={orientation}
            isRange
            hideLabel={!!props.hideValueLabel}
            hideInput={shouldHideInput}
            stepValue={step}
            stepMediumValue={stepMedium}
            stepLargeValue={stepLarge}
            gripLabelClassName={props.gripLabelClassName}
            renderGripInput={props.renderGripInput}
            handleLabelTokens={props.handleLabelTokens}
            inputAriaLabel={localize('slider.numericValueInput.aria')}
            ariaLabel={props.maxGripAriaText ?? localize('sliderGrip.sliderMaxThumb.aria')}
            arrowGrips={arrowGrips}
            onDragStart={
              props.disabled || props.readOnly
                ? undefined
                : () => setLastDraggedHandleIndex([index, 'end'])
            }
            onChange={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    value.onChange({ minValue: adjustedMinValue, maxValue: e.value });
                  }
            }
            onCommit={
              props.disabled || props.readOnly
                ? undefined
                : (e) => {
                    value.onCommit &&
                      value.onCommit({ minValue: adjustedMinValue, maxValue: e.value });
                  }
            }
            getSliderClientRect={getSliderClientRect}
          />
        </Fragment>
      );
    }
  });

  const dt = useDesignTokens();
  const trackToken = renderInlineCssVars(getSliderTrackThemeTokens(dt), 'slider-track');
  // the slider gets a role of group unless there is only one thumb and no input
  const isGroup =
    props.values[0] && props.values[0].type === 'range'
      ? true
      : !props.hideInput && !props.hideValueLabel && !props.readOnly && !props.disabled;

  // if there is no ariaLabelledBy, set the aria label with ariaLabel or the default
  const ariaLabel = props.ariaLabelledBy
    ? undefined
    : props.ariaLabel ?? localize('slider.default.aria');

  const tokens = renderInlineCssVars(
    {
      'spacing-xs': dt.spacing.core.xs,
    },
    'slider'
  );

  return (
    <div
      className={cx(
        cn.container[orientation],
        !props.ticks && !props.tickLabels && cn.noTicks,
        props.className
      )}
      style={{ ...tokens, ...props.style }}
      data-testid={props.testId}
      id={props.id}
      aria-label={isGroup ? ariaLabel : undefined}
      aria-labelledby={!isGroup ? undefined : props.ariaLabelledBy}
      role={isGroup ? 'group' : undefined}
      ref={sliderRef}
    >
      <SliderTickLabels
        testId={postfixTestId(props.testId, 'tickLabels')}
        disabled={props.disabled}
        ticks={ticks}
        tickIds={tickIds}
        tickLabels={props.tickLabels}
        format={formatter}
        scale={scale}
        orientation={orientation}
        labelTokens={props.tickLabelTokens}
        ref={sliderTickLabelsRef}
      />
      <div
        className={cx(cn.track[orientation], props.trackClassName)}
        ref={refTrack}
        style={{ ...trackToken, ...props.trackStyle }}
        data-testid={postfixTestId(props.testId, 'track')}
        onClick={onTrackClick}
      >
        <SliderTrackTicks
          testId={postfixTestId(props.testId, 'trackTicks')}
          orientation={orientation}
          tickPositions={ticks.map((v, index, array) => {
            if (v === scale.maxValue && index === array.length - 1) {
              // NOTE: handles case when scale.minValue === scale.maxValue
              return 1;
            }
            return scale.position(v);
          })}
        />
      </div>
      {valueElements}
    </div>
  );
};

const getSliderTrackThemeTokens = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      bd: dt.c.colorUiBorder,
      bg: dt.c.colorUiDark,
      'top-bd': dt.c.colorUiBorder,
    };
  } else {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          bg: dt.c.black,
          bd: dt.c.white,
          'top-bd': dt.c.white,
        };
    }
    switch (dt.themeVariant) {
      case 'high-contrast':
        return {
          bg: dt.c.n_5, // n_6 in livingston?
          bd: dt.c.n2,
          'top-bd': dt.c.n2,
        };
      case 'dark':
        return {
          bg: dt.c.p_3,
          bd: setAlpha(dt.c.white, 0.3),
          'top-bd': dt.c.p_2,
        };
      default:
        return {
          bg: dt.c.n5,
          bd: dt.c.n3,
          'top-bd': dt.c.n_1,
        };
    }
  }
};

function resolveGripBoundary(
  left: SliderValue | RangeSliderValue | undefined,
  right: SliderValue | RangeSliderValue | undefined,
  scale: SliderScale
) {
  const bounds: [number, number] = [0, 0];
  if (left === undefined) {
    bounds[0] = scale.minValue;
  } else if (left.type === 'range') {
    bounds[0] = left.maxValue;
  } else {
    bounds[0] = left.value;
  }
  if (right === undefined) {
    bounds[1] = scale.maxValue;
  } else if (right.type === 'range') {
    bounds[1] = right.minValue;
  } else {
    bounds[1] = right.value;
  }
  return bounds;
}

function adjustValueForScale(value: number, lowerBoundValue: number, upperBoundValue: number) {
  let adjustedValue = Math.max(value, lowerBoundValue);
  adjustedValue = Math.min(adjustedValue, upperBoundValue);
  return adjustedValue;
}
